import * as React from "react";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import { Helmet } from "react-helmet";
import { withToastManager } from "react-toast-notifications";
import { RouteComponentProps } from "react-router";

/** Presentation/UI */
import Table from "../../Components/Table";
import BackendWrapper from "../../Components/Layouts/BackendWrapper";
import PageLoader from "../../Components/PageLoader";
import {
  TableHeader,
  TableHeaderContainer
} from "../../Components/Styled/ListViewElements";
import Row from "../../Components/Styled/Row";
import StyledButton from "../../Components/Styled/Button";
import { MdSearch } from "react-icons/md";
import { Colors } from "../../Themes";

/** Custom Types */
import { UserRole } from "../../API";
import { ToastNotificationType } from "../../CustomTypes";
import { TableConsts } from "../../Utils/Consts";
import { sortByCreatedAt } from "../../Utils/Helpers";
import { listSales } from "../../graphql/custom-queries";

import GlobalModalContainer from "../../Components/Modal";
import CompanyView from "../Companies/CompanyView";

type Props = RouteComponentProps & {
  toastManager: ToastNotificationType;
};

type State = {
  importModal: boolean;
  searchFilter: boolean;
  companyModal: boolean;
  companyData: {};
};

class Sales extends React.Component<Props> {
  state: State = {
    importModal: false,
    searchFilter: false,
    companyModal: false,
    companyData: {}
  };

  // Toggle the searcch filter
  toggleSearchFilter = (): void => {
    this.setState({ searchFilter: !this.state.searchFilter });
  };

  /** Success notification */
  toastNotification = (message: string, appearance?: string) => {
    this.props.toastManager.add(message, {
      appearance: appearance ? appearance : "success",
      autoDismiss: true
    });
  };

  // Format & show report purchased date
  formatDate = (date: string) => {
    const purchasedDate = new Date(date);
    const day = purchasedDate.getDate();
    const month = purchasedDate.getMonth() + 1;
    const year = purchasedDate.getFullYear();
    return day + "-" + month + "-" + year;
  };

  // Show modal with company information
  showCompanyModal = (companyData: {}) => {
    this.setState({
      companyModal: true,
      companyData
    });
  };

  // Close opened modal
  closeModal = (): void => {
    this.setState({
      companyModal: false
    });
  };

  render() {
    const { searchFilter, companyData, companyModal } = this.state;

    return (
      <BackendWrapper>
        <Query
          query={gql(listSales)}
          variables={{
            limit: TableConsts.limit
          }}
        >
          {({ loading, error, data }: any) => {
            if (loading) {
              return <PageLoader />;
            }

            if (error) {
              return <div>There was a problem loading your data</div>;
            }

            if (!data || !data.listTransactions) {
              return <Row>There are no Reports at the moment</Row>;
            }

            return (
              <React.Fragment>
                <TableHeaderContainer>
                  <TableHeader>
                    <span>Transactions History</span>
                  </TableHeader>
                  <StyledButton
                    type="button"
                    label={<MdSearch size="1.3em" color={Colors.secondary} />}
                    width="auto"
                    margin="0px 10px"
                    onClick={this.toggleSearchFilter}
                    color={searchFilter ? Colors.background : Colors.coal}
                    background={
                      searchFilter ? Colors.default : Colors.background
                    }
                  />
                </TableHeaderContainer>
                <Table
                  data={sortByCreatedAt(data.listTransactions.items)}
                  getTdProps={(
                    state: any,
                    rowInfo: { [key: string]: any },
                    columnInfo: { [key: string]: any }
                  ) => {
                    return {
                      onClick: () => {
                        if (rowInfo) {
                          if (columnInfo.id === "report.name") {
                            this.props.history.push({
                              pathname: `/admin-reports/preview`,
                              state: rowInfo.original.report
                            });
                          }
                          if (columnInfo.id === "company.name") {
                            this.showCompanyModal(rowInfo.original.company);
                          }
                        }
                      }
                    };
                  }}
                  columns={[
                    {
                      Header: "Report Name",
                      accessor: "report.name",
                      sortable: true,
                      filterable: searchFilter
                    },
                    {
                      id: "purchaseDate",
                      Header: "Purchase Date",
                      accessor: (date: { [key: string]: string }) => {
                        return this.formatDate(date.createdAt);
                      },
                      sortable: false
                    },
                    {
                      Header: "Company",
                      accessor: "company.name",
                      sortable: true,
                      filterable: searchFilter
                    },
                    {
                      id: "price",
                      Header: "Price",
                      accessor: (item: { [key: string]: any }) => {
                        return "R " + item.report.price;
                      },
                      sortable: true,
                      filterable: searchFilter
                    },
                    {
                      id: "paid",
                      Header: "Paid",
                      accessor: (item: { [key: string]: any }) => {
                        return "R " + item.cost;
                      },
                      sortable: true
                    }
                  ]}
                  defaultPageSize={TableConsts.defaultRows}
                  showPaginationBottom={
                    data.listTransactions.items.length > TableConsts.defaultRows
                  }
                />
              </React.Fragment>
            );
          }}
        </Query>
        <GlobalModalContainer
          toggleModal={this.closeModal}
          title="View Company Details"
          modalDisplay={
            <CompanyView
              roles={[UserRole.ROOTS_SUPER_ADMIN, UserRole.ROOTS_ADMIN]}
              notification={this.toastNotification}
              closeModal={this.closeModal}
              companyData={companyData}
              {...this.props}
            />
          }
          modal={companyModal}
        />
        <Helmet title="View Purchased Reports" />
      </BackendWrapper>
    );
  }
}

export default withToastManager(Sales);
